import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Refresh from "@mui/icons-material/Refresh";
import DragPreviewLayer from "designSystem/dnd/components/DragPreviewLayer";
import { ColumnDropDividerWrapper, ColumnDropWrapper, DndStore, DndStoreContext } from "designSystem/dnd/index";
import { useSyncLocalStore } from "designSystem/hooks";
import { Box, Button, FlexRow, FlexSideDrawer, SingleSelect, Text } from "designSystem/index";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import { CompositionMaterial } from "../Composition/CompositionStore";
import BaseCompositionApplyToMenu from "./BaseCompositionApplyToMenu";
import BaseCompositionPlacementSingleItem from "./BaseCompositionPlacementSingleItem";
import BaseCompositionRightDrawer from "./BaseCompositionRightDrawer";
import { useBaseCompositionStore } from "./BaseCompositionStore";
import { CompositionPlacement } from "./CompositionPlacement";
import ErrorMessageBanners from "./ErrorMessageBanners";
import { SalesRegionException } from "./SalesRegionException";

interface BaseCompositionMainPanelProps {
	salesRegionException: SalesRegionException;
}

const BaseCompositionMainPanel: FunctionComponent<BaseCompositionMainPanelProps> = observer(function BaseCompositionMainPanel({ salesRegionException }) {
	const {
		allBOMsOnStyle,
		applyToSalesRegionNameDropdown,
		apparelBOMValueAsComponentItem,
		fromStyleBom,
		getSelectActions,
		handleApplyToMenuClick,
		onClickApparelBOMGoToDialog,
		resetExceptionOnClick,
		sameAsBaseComposition,
		setCurrentBOM,
	} = useBaseCompositionStore();

	const {
		addCompositionPlacement,
		isBaseComposition,
		compositionPlacements,
		disableNewPlacement,
		onDropPlacement,
	} = salesRegionException;

	const dndStore = useSyncLocalStore(() => new DndStore<CompositionMaterial>());
	const { dragItem } = dndStore;

	return (
		<FlexRow
			fullHeight
		>
			<DndStoreContext.Provider value={dndStore}>
				<ColumnDropWrapper
					data-csi-automation="SelectBOM"
					dropTypes={["CompositionPlacement"]}
					flexGrow={1}
					gap={1}
					isRootDropWrapper
					onDrop={onDropPlacement}
					overflow="auto"
					padding="12px 24px 24px 24px"
				>
					<ErrorMessageBanners />
					{!isBaseComposition && (
						<FlexRow
							sx={{ justifyContent: "end" }}
						>
							<Button
								design="link"
								disabled={sameAsBaseComposition}
								onClick={resetExceptionOnClick}
								res="ResetException"
								startIcon={<Refresh />}
							/>
							{applyToSalesRegionNameDropdown.length > 0 && (
								<Button
									data-csi-automation="button-SalesRegionExceptionApplyTo"
									design="link"
									endIcon={<ArrowDropDownIcon />}
									onClick={handleApplyToMenuClick}
									res="ApplyTo"
								/>
							) }
						</FlexRow>
					)}
					<BaseCompositionApplyToMenu />
					{fromStyleBom && (
						<Text
							bold
							data-csi-automation="text-SelectBOM"
							res="CareAndComp_SelectBOM"
							size={12}
						/>
					)}
					{fromStyleBom && (
						<SingleSelect
							dataCsiAutomation="Select-BOM"
							disableClearable
							disabled={!isBaseComposition}
							fullWidth
							onChange={setCurrentBOM}
							options={allBOMsOnStyle}
							placeHolderRes="CareAndComp_SelectBOMplaceholder"
							selectActions={allBOMsOnStyle.length > 0 ? getSelectActions(onClickApparelBOMGoToDialog) : null}
							value={apparelBOMValueAsComponentItem}
						/>
					)}
					<Box>
						<ColumnDropDividerWrapper gap={1}>
							{compositionPlacements.map((compositionPlacement: CompositionPlacement, index: number) => {
								return ( 
									<BaseCompositionPlacementSingleItem 
										currentPlacement={compositionPlacement}
										index={index}
										key={compositionPlacement.key}
										salesRegionException={salesRegionException}
									/>
								);
							})}
						</ColumnDropDividerWrapper>
					</Box>
					<Button
						design="secondary"
						disabled={disableNewPlacement}
						onClick={() => addCompositionPlacement()}
						res="CompositionPlacement"
						startIcon={<AddIcon />}
						sx={{ padding: 1, maxWidth: "159px" }}
					/>
				</ColumnDropWrapper>
				{dragItem instanceof CompositionPlacement && (
					<DragPreviewLayer
						previewComponent={(
							<BaseCompositionPlacementSingleItem
								currentPlacement={dragItem}
								isDragging
								salesRegionException={salesRegionException}
							/>
						)}
					/>
				)}
			</DndStoreContext.Provider>
			<FlexSideDrawer
				disabled
				drawerNode={<BaseCompositionRightDrawer salesRegionException={salesRegionException} />}
				open
				rightDrawer
				widthOpen={400}
			/>
		</FlexRow>
	);});

export default BaseCompositionMainPanel;